import { ICollidable, Bounds, CollisionUtils } from '../collision/types';
import { Pit } from '../pit';

/**
 * Abstract base class for objects with physics (gravity, ground/pit collision)
 */
export abstract class PhysicsObject implements ICollidable {
	x: number;
	y: number;
	width: number;
	height: number;
	vx: number = 0;
	vy: number = 0;

	abstract readonly collisionType: string;

	protected gravity: number = 0.5;
	protected groundY: number;
	protected isGrounded: boolean = false;

	// Pit state
	protected isInPit: boolean = false;
	protected currentPitBounds: Bounds | null = null;
	protected pitBottomY: number | null = null;

	constructor(x: number, y: number, width: number, height: number, groundY: number) {
		this.x = x;
		this.y = y;
		this.width = width;
		this.height = height;
		this.groundY = groundY;
	}

	getBounds(): Bounds {
		return {
			x: this.x,
			y: this.y,
			width: this.width,
			height: this.height,
		};
	}

	abstract isActive(): boolean;
	abstract onCollision(other: ICollidable): void;

	/**
	 * Ground offset for sprite padding (override in subclass if needed)
	 */
	protected getGroundOffset(): number {
		return 0;
	}

	/**
	 * Called when object reaches pit bottom (override in subclass)
	 */
	protected abstract onPitBottom(): void;

	/**
	 * Handle collision with pit - shared logic for all physics objects
	 */
	handlePitCollision(pit: Pit): void {
		const pitBounds = pit.getBounds();

		// Check if object's center of gravity (bottom-center) is horizontally over the pit
		const cog = CollisionUtils.getCenterOfGravity(this.getBounds());
		const isOverPit = cog.x >= pitBounds.x && cog.x <= pitBounds.x + pitBounds.width;

		if (!isOverPit) {
			// Not over pit, reset pit state
			this.isInPit = false;
			this.currentPitBounds = null;
			this.pitBottomY = null;
			return;
		}

		// Store pit bounds for update() to use
		this.currentPitBounds = pitBounds;
		this.pitBottomY = pitBounds.y + pitBounds.height;

		// Mark as in pit to allow falling through
		this.isInPit = true;

		// If already on ground, give small push to start falling
		const groundLimit = this.groundY - this.height + this.getGroundOffset();
		if (this.y >= groundLimit && this.vy === 0) {
			this.vy = 0.1;
		}
	}

	/**
	 * Apply physics (gravity, ground/pit collision)
	 * Call this from subclass update() method
	 */
	protected applyPhysics(): void {
		// Gravity
		this.vy += this.gravity;

		// Apply velocity
		this.x += this.vx;
		this.y += this.vy;

		// Handle pit falling
		if (this.isInPit && this.currentPitBounds !== null && this.pitBottomY !== null) {
			// Constrain to pit walls (horizontal)
			this.x = constrain(
				this.x,
				this.currentPitBounds.x,
				this.currentPitBounds.x + this.currentPitBounds.width - this.width
			);

			// Check if reached pit bottom
			const pitFloor = this.pitBottomY - this.height;
			if (this.y >= pitFloor) {
				this.y = pitFloor;
				this.vy = 0;
				this.vx = 0;
				this.onPitBottom();
				return;
			}
			// Continue falling through pit, skip ground collision
		} else {
			// Normal ground collision
			const groundLimit = this.groundY - this.height + this.getGroundOffset();
			if (this.y >= groundLimit) {
				this.y = groundLimit;
				this.vy = 0;
				this.isGrounded = true;
			} else {
				this.isGrounded = false;
			}
		}
	}

	/**
	 * Reset pit state (call from subclass reset if needed)
	 */
	protected resetPitState(): void {
		this.isInPit = false;
		this.currentPitBounds = null;
		this.pitBottomY = null;
	}
}
